/**
 * SpotlightOverlay — Spec 10-2 Part 3
 * Temporarily highlights a specific UI element by rendering a dark overlay
 * with a cutout around the target.
 */
import { useEffect, useState, useCallback } from 'react';

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 8;
const MOBILE_PADDING = 16;
const DISMISS_TIMEOUT = 5000;

export function SpotlightOverlay() {
  const [targetKey, setTargetKey] = useState<string | null>(null);
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const [visible, setVisible] = useState(false);

  const dismiss = useCallback(() => {
    setVisible(false);
    setTimeout(() => { setTargetKey(null); setRect(null); }, 200);
  }, []);

  // Listen for spotlight events
  useEffect(() => {
    const handler = (e: Event) => {
      const key = (e as CustomEvent<{ target: string }>).detail?.target;
      if (!key) return;
      setTargetKey(key);
    };
    window.addEventListener('stevens:spotlight', handler);
    return () => window.removeEventListener('stevens:spotlight', handler);
  }, []);

  // Find element and compute rect
  useEffect(() => {
    if (!targetKey) return;

    const el = document.querySelector(`[data-spotlight="${targetKey}"]`);
    if (!el) {
      // Target not found — fail gracefully
      setTargetKey(null);
      return;
    }

    const isMobile = window.matchMedia('(pointer: coarse)').matches;
    const pad = isMobile ? MOBILE_PADDING : PADDING;
    const bounds = el.getBoundingClientRect();

    setRect({
      top: bounds.top - pad,
      left: bounds.left - pad,
      width: bounds.width + pad * 2,
      height: bounds.height + pad * 2,
    });
    setVisible(true);

    // Auto-dismiss after 5 seconds
    const timer = setTimeout(dismiss, DISMISS_TIMEOUT);
    return () => clearTimeout(timer);
  }, [targetKey, dismiss]);

  // Escape key dismisses
  useEffect(() => {
    if (!visible) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [visible, dismiss]);

  if (!visible || !rect) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] transition-opacity duration-200"
      style={{ opacity: visible ? 1 : 0 }}
      onClick={dismiss}
      role="presentation"
      aria-hidden="true"
    >
      {/* Dark overlay with cutout */}
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          <mask id="spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={rect.left}
              y={rect.top}
              width={rect.width}
              height={rect.height}
              rx={8}
              fill="black"
            />
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.4)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Pulse ring around target */}
      <div
        className="absolute rounded-lg ring-2 ring-primary animate-pulse pointer-events-none"
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          animationIterationCount: 2,
        }}
      />
    </div>
  );
}
